import React, { useEffect, useState } from 'react';
import { Sparkles, RefreshCw } from 'lucide-react';
import { SlotCard, ContentSlot } from '../../components/admin/SlotCard';

export const PropozycjePostowPage: React.FC = () => {
    const [slots, setSlots] = useState<ContentSlot[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [count, setCount] = useState(12);
    const [err, setErr] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        fetch('/api/admin/content?status=draft&limit=200', { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
            .then(d => setSlots(d.slots || []))
            .catch(e => setErr(String(e)))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const generate = async () => {
        setGenerating(true);
        setErr(null);
        try {
            const r = await fetch('/api/admin/content/generate-batch', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ count }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || d.error || 'failed');
            load();
        } catch (e: any) {
            setErr(`Generation failed: ${e.message}`);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <h1 className="text-2xl font-bold">Propozycje postów ({slots.length})</h1>
                <div className="flex items-center gap-2">
                    <input
                        type="number"
                        min={1}
                        max={30}
                        value={count}
                        onChange={e => setCount(Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 12)))}
                        className="w-16 px-2 py-1.5 border rounded-md bg-bkg-light dark:bg-bkg-dark text-sm"
                    />
                    <button
                        onClick={generate}
                        disabled={generating}
                        className="px-4 py-2 bg-primary-light dark:bg-primary-dark text-white rounded-md font-semibold flex items-center gap-2 disabled:opacity-50"
                    >
                        {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {generating ? 'Generuję…' : 'Wygeneruj nowe'}
                    </button>
                    <button onClick={load} className="px-3 py-2 bg-bkg-light dark:bg-bkg-dark rounded-md flex items-center gap-2 text-sm">
                        <RefreshCw className="w-4 h-4" />
                        Odśwież
                    </button>
                </div>
            </div>

            {err && <div className="bg-red-500/10 text-red-600 px-3 py-2 rounded-md text-sm mb-4">{err}</div>}

            {loading && <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Ładowanie…</div>}

            {!loading && slots.length === 0 && (
                <div className="bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark p-8 text-center">
                    <Sparkles className="w-10 h-10 mx-auto mb-3 text-text-secondary-light dark:text-text-secondary-dark" />
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
                        Brak propozycji do akceptacji. Wygeneruj batch — LLM stworzy {count} postów na 7 dni (FB+IG, balance pillars × 5 SKU).
                    </p>
                    <button
                        onClick={generate}
                        disabled={generating}
                        className="px-6 py-2 bg-primary-light dark:bg-primary-dark text-white rounded-md font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {generating ? 'Generuję…' : `Wygeneruj ${count} propozycji`}
                    </button>
                </div>
            )}

            {!loading && slots.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {slots.map(s => (
                        <SlotCard
                            key={s.id}
                            slot={s}
                            onAction={(action, updated) => {
                                if (action === 'approved' || action === 'rejected') {
                                    setSlots(slots.filter(x => x.id !== s.id));
                                } else if (action === 'updated') {
                                    setSlots(slots.map(x => x.id === s.id ? updated : x));
                                }
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
